<template>
  <v-dialog
    scrollable
    :value="show"
    width="auto"
    @click:outside="$emit('close')"
  >
    <v-card class="pa-4">
      <v-card-text class="ma-0 pa-0 d-flex flex-column">
        <div class="d-flex justify-space-between">
          <div class="d-flex flex-column me-10" style="width: 363px;">
            <p
              class="ma-0 pa-0 blue-grey--text text--darken-3 subtitle-1 font-weight-medium"
            >
              {{ name }}
            </p>

            <p
              class="blue-grey--text text--darken-1 body-2 pa-0 mt-0"
              style="white-space: pre-line;"
            >
              {{ description }}
            </p>

            <div v-if="collaborators.length" class="mt-4">
              <p class="caption blue-grey--text text--darken-1 ma-0 pa-0 mb-2">
                {{ $t('common.collaborator_other') }}
              </p>
              <AssigneesSelection
                disabled
                :accounts="collaborators"
                :maxItemsToShow="10"
                @click="trackAllItems({ collaborators: collaborators.length })"
              />
            </div>
          </div>

          <div
            class="d-flex flex-column"
            style="min-width: 120px; max-width:250px"
          >
            <div v-if="routineDefinition.frequency">
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('routines.frequency_label') }}
              </p>
              <v-chip
                small
                color="blue-grey lighten-3"
                class="mb-4 caption blue-grey--text text--darken-3 cursor-default"
                outlined
              >
                {{ $t(`routines.frequency.${routineDefinition.frequency}`) }}
              </v-chip>
            </div>

            <div v-if="routineDefinition.type">
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('common.routine_type') }}
              </p>
              <v-chip
                small
                color="blue-grey lighten-3"
                class="mb-4 caption blue-grey--text text--darken-3 cursor-default"
                outlined
              >
                {{ $t(`routines.type.${routineDefinition.type}`) }}
              </v-chip>
            </div>

            <div v-if="showSituationTypes">
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('settings.situation_types') }}
              </p>
              <ChipList
                class="mb-4"
                outlined
                :totalItems="routineSituationTypes.length"
                :items="routineSituationTypes"
                :limit="1"
                @click="
                  trackAllItems({ situationTypes: situationTypes.length })
                "
              />
            </div>

            <div v-if="showCategories">
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('common.category_other') }}
              </p>
              <ChipList
                class="mb-4"
                outlined
                :totalItems="routineCategories.length"
                :items="routineCategories"
                :limit="1"
                @click="trackAllItems({ categories: categories.length })"
              />
            </div>
            <div v-if="routinePresetTags.length">
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('common.attribute_other') }}
              </p>
              <ChipList
                class="mb-4"
                outlined
                :totalItems="routinePresetTags.length"
                :items="routinePresetTags"
                :limit="1"
                @click="trackAllItems({ presetTags: presetTags.length })"
              />
            </div>

            <div>
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('routines.created_in') }}
              </p>
              <v-chip
                small
                color="blue-grey lighten-3"
                class="mb-4 caption blue-grey--text text--darken-3 cursor-default"
                outlined
              >
                {{ location.site.name }}
              </v-chip>
            </div>

            <div class="mb-4">
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('common.created_by') }}
              </p>
              <ChipAvatar small :account="routineDefinition.createdBy" />
            </div>

            <div
              v-if="routineDefinition.createdAt"
              data-testid="routineDetailsDialog-text-creationDate"
              :title="formatDate(routineDefinition.createdAt, 'MMM D YYYY, hh:mm a')"
            >
              <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
                {{ $t('common.created_on') }}
              </p>
              <v-chip
                data-testid="routineDetailsDialog-chip-creationDate"
                small
                color="blue-grey lighten-3"
                class="mb-4 caption blue-grey--text text--darken-3 cursor-default"
                outlined
              >
                {{ formatDate(routineDefinition.createdAt) }}
              </v-chip>
            </div>
          </div>
        </div>

        <v-divider class="my-6" />

        <p class="subtitle-2 blue-grey--text text--darken-3 ma-0 pa-0 mb-4">
          {{ $t('common.assignee_other') }}
        </p>
        <div v-if="routineDefinition.isGloballyAssigned">
          <v-chip small class="routine-global-chip">
            <v-icon size="16" color="blue" class="me-1">mdi-earth </v-icon>
            <p class="ma-0 pa-0 blue--text caption">
              {{ $t('routines.extent_card_global_title') }}
            </p>
          </v-chip>
        </div>

        <div v-else>
          <div v-if="assignedSites.length">
            <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
              {{ $t('common.site_other') }}
            </p>
            <ChipList
              class="mb-4"
              outlined
              :totalItems="assignedSites.length"
              :items="assignedSites"
              :limit="1"
              @click="trackAllItems({ assignedSites: assignedSites.length })"
            />
          </div>

          <div v-if="assignedTeams.length">
            <p class="caption blue-grey--text text--darken-1 mb-2 pa-0">
              {{ $t('common.team_other') }}
            </p>
            <ChipList
              class="mb-4"
              outlined
              :totalItems="assignedTeams.length"
              :items="assignedTeams"
              :limit="1"
              @click="trackAllItems({ assignedTeams: assignedTeams.length })"
            />
          </div>

          <div v-if="assignees.length">
            <p class="caption blue-grey--text text--darken-1 ma-0 pa-0 mb-2">
              {{ $t('common.users.user_other') }}
            </p>
            <AssigneesSelection
              disabled
              :accounts="assignees"
              :maxItemsToShow="10"
              @click="trackAllItems({ assignees: assignees.length })"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions
        v-if="canEditRoutine"
        class="d-flex justify-end py-0 mt-6 buttons-container"
      >
        <Button
          size="small"
          icon="mdi-pencil-outline"
          color="primary"
          :text="$t('common.button.edit')"
          @click="handleEdit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { utils } from 'frontend-js-shared'
import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import AssigneesSelection from '@/components/AssigneesSelector/AssigneesSelection.vue'
import Button from '@/components/UI/Button'
import ChipList from '@/components/ChipList.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'

export default {
  name: 'RoutineDetailsDialog',

  mixins: [mixin, trackerMixin, routineDefinitionPermissions],

  props: {
    show: Boolean,
    routineDefinition: Object,
  },

  components: {
    AssigneesSelection,
    Button,
    ChipList,
    ChipAvatar,
  },

  computed: {
    ...mapGetters(['organizationSites', 'organizationTags']),

    name() {
      return this.routineDefinition?.name ?? ''
    },

    description() {
      return this.routineDefinition?.description ?? ''
    },

    showSituationTypes() {
      return Boolean(this.routineDefinition?.presetSituationTypes?.length)
    },

    showCategories() {
      return Boolean(this.routineDefinition?.categories?.length)
    },

    routinePresetTags() {
      return this.fullPresetTags.map(tag => {
        return tag.name
      })
    },

    fullPresetTags() {
      return this.organizationTags?.filter?.(tag => {
        return this.routineDefinition?.presetTags?.includes(tag.id) ?? []
      })
    },

    location() {
      return {
        site: this.routineDefinition?.site,
      }
    },

    routineSituationTypes() {
      return this.routineDefinition.presetSituationTypes.map(st => {
        return this.$t(
          `quick_action.situation_types.situations.${st.situation}`
        )
      })
    },

    routineCategories() {
      return this.routineDefinition.categories.map(category => {
        return this.$t(`common.categories.${category}`)
      })
    },

    collaborators() {
      return this.routineDefinition?.collaborators ?? []
    },

    assignedSites() {
      if (this.routineDefinition?.assignedSites?.length === 0) return []
      return this.routineDefinition?.assignedSites.map(site => {
        return site.name
      })
    },

    assignedTeams() {
      if (this.routineDefinition?.assignedTeams?.length === 0) return []
      return this.routineDefinition?.assignedTeams.map(team => {
        return team.name
      })
    },

    assignees() {
      return this.routineDefinition?.assignees ?? []
    },

    canEditRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.routineDefinition,
      }).edit
    },
  },

  methods: {
    formatDate(date, format = 'MMMM DD YYYY') {
      return utils.dates.formatDate(date, format)
    },

    handleEdit() {
      this.trackRoutineViewNavigate({
        action: 'goToEditView',
        id: this.routineDefinition?.id,
        source: 'routineDetailsDialog',
      })
      this.navigateTo({
        name: 'editRoutine',
        params: { routineDefinitionId: this.routineDefinition?.id ?? '' },
      })
    },

    trackAllItems(data) {
      this.trackViewAllItems({
        ...data,
        source: 'routineDetailDialog',
        entityType: 'routine',
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.buttons-container {
  position: sticky;
  bottom: 0;
  background-color: $color-white;
}
</style>

<style lang="scss">
.routine-global-chip {
  border: 1px solid $color-blue-lighten1 !important;
  background-color: $color-blue-lighten2 !important;
}
</style>
